import { useState } from 'react';
import { Search, Key, Globe, BarChart3, BookOpen, Code, LogOut, Menu, X, Link2, Palette, ShieldCheck, Users, CreditCard } from 'lucide-react';
import { User } from '../lib/api';
import Overview from './Overview';
import ApiKeyManager from './ApiKeyManager';
import DomainSettings from './DomainSettings';
import CourseManager from './CourseManager';
import Analytics from './Analytics';
import SetupGuide from './SetupGuide';
import ClickFunnelsIntegration from './ClickFunnelsIntegration';
import WidgetManager from './WidgetManager';
import AdminPanel from './AdminPanel';
import MemberManager from './MemberManager';
import BillingManager from './BillingManager';

interface DashboardProps {
  user: User;
  onLogout: () => void;
}

export default function Dashboard({ user, onLogout }: DashboardProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isOwnerOrAdmin = user.role === 'owner' || user.role === 'admin';
  const canEdit = isOwnerOrAdmin || user.role === 'editor';

  const navigation = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    ...(isOwnerOrAdmin ? [{ id: 'api-keys', name: 'API Keys', icon: Key }] : []),
    ...(isOwnerOrAdmin ? [{ id: 'domains', name: 'Domains', icon: Globe }] : []),
    ...(canEdit ? [{ id: 'courses', name: 'Courses', icon: BookOpen }] : []),
    ...(isOwnerOrAdmin ? [{ id: 'clickfunnels', name: 'Course Platform', icon: Link2 }] : []),
    { id: 'analytics', name: 'Analytics', icon: Search },
    ...(canEdit ? [{ id: 'widget', name: 'Widget', icon: Palette }] : []),
    { id: 'setup', name: 'Setup Guide', icon: Code },
    ...(isOwnerOrAdmin ? [{ id: 'members', name: 'Team', icon: Users }] : []),
    ...(user.role === 'owner' ? [{ id: 'billing', name: 'Billing', icon: CreditCard }] : []),
    ...(user.is_admin ? [{ id: 'admin', name: 'Admin', icon: ShieldCheck }] : []),
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <Overview onNavigate={setActiveTab} />;
      case 'api-keys':
        return <ApiKeyManager />;
      case 'domains':
        return <DomainSettings />;
      case 'courses':
        return <CourseManager />;
      case 'clickfunnels':
        return <ClickFunnelsIntegration userPlan={user.plan} />;
      case 'analytics':
        return <Analytics />;
      case 'widget':
        return <WidgetManager />;
      case 'setup':
        return <SetupGuide />;
      case 'members':
        return <MemberManager />;
      case 'billing':
        return <BillingManager userRole={user.role} currentPlan={user.plan} />;
      case 'admin':
        return user.is_admin ? <AdminPanel /> : <Overview />;
      default:
        return <Overview />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Search className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-gray-900">CourseSearch</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* User info */}
          <div className="px-4 py-3 border-b bg-gray-50">
            <p className="text-sm font-medium text-gray-900 truncate">{user.company_name}</p>
            <p className="text-xs text-gray-500 truncate">{user.name || user.email}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                {user.plan} Plan
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
                user.role === 'owner' ? 'bg-purple-100 text-purple-700' :
                user.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                user.role === 'editor' ? 'bg-green-100 text-green-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {user.role}
              </span>
              {user.is_admin && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                  Admin
                </span>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isAdmin = item.id === 'admin';
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isAdmin && activeTab !== item.id
                      ? 'text-purple-700 hover:bg-purple-50'
                      : activeTab === item.id
                        ? isAdmin ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </button>
              );
            })}
          </nav>

          {/* Logout button */}
          <div className="border-t p-4">
            <button
              onClick={onLogout}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900 capitalize">
              {navigation.find((n) => n.id === activeTab)?.name || 'Dashboard'}
            </h1>
            <div className="w-6 lg:hidden" /> {/* Spacer for centering */}
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}
